import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Plus, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { fetchRefills, formatUsd } from "../lib/api";
import { useCart } from "../context/CartContext";

// Curated lookbook stories — mix of AI-generated + editorial Pexels imagery
// matched to the warm sanctuary palette. Each story pins to 1–3 refill IDs
// so the reader can one-tap-add the exact products in the frame.
const STORIES = [
  {
    id: "morning-ritual",
    image: "/generated/look_hydration.png",
    imageRatio: "aspect-[4/5]",
    tag: "Week 2 · Morning",
    title: "The 8am pour.",
    story:
      "A slow morning is the whole point. I fill my bottle, sit by the window, and take my first Alastin dose of the day. Nothing else on the calendar.",
    author: "— Priya · Facelift, Age 47",
    pins: ["larq-purevis-2-self-cleaning-bottle", "alastin-regenerating-skin-nectar-with-trihex"],
    palette: "bg-[#FDF3ED]",
  },
  {
    id: "scar-ritual",
    image: "/generated/look_applying_scar_gel.png",
    imageRatio: "aspect-[4/5]",
    tag: "Week 4 · Evening",
    title: "The two-minute silicone habit.",
    story:
      "Sixty seconds morning, sixty seconds night. Four weeks in, the line is barely there. It's the smallest habit that made the biggest difference.",
    author: "— Sofia · Tummy Tuck, Age 34",
    pins: ["silagen-100-pure-silicone-gel-15g", "biocorneum-silisilk-advanced-scar-gel"],
    palette: "bg-white",
  },
  {
    id: "cryo-restore",
    image: "https://images.pexels.com/photos/3762668/pexels-photo-3762668.jpeg?auto=compress&cs=tinysrgb&h=1200&w=900",
    imageRatio: "aspect-[4/5]",
    tag: "Day 3 · Post-Op",
    title: "Fifteen minutes of quiet.",
    story:
      "The Charlotte Tilbury cryo mask on, my playlist on, my dog nearby. Somewhere in the fifteen minutes I stop feeling like a patient.",
    author: "— Amara · Facelift, Age 52",
    pins: ["charlotte-tilbury-cryo-recovery-face-mask", "boiron-arnicare-gel-2-6-oz"],
    palette: "bg-[#FDF3ED]",
  },
  {
    id: "unboxing",
    image: "/generated/hero_mommy.png",
    imageRatio: "aspect-[4/5]",
    tag: "Day -3 · The Kit Arrives",
    title: "Opening the box felt like a gift.",
    story:
      "It's the first thing that made me feel taken care of before I'd even been taken care of. Every item had a purpose and I understood exactly what to do.",
    author: "— Naomi · Mommy Makeover, Age 39",
    pins: ["marena-recovery-full-body-faja", "marena-caress-pocketed-recovery-bra", "skinceuticals-advanced-scar-control-95-silicone"],
    palette: "bg-white",
  },
  {
    id: "night-routine",
    image: "https://images.pexels.com/photos/6663493/pexels-photo-6663493.jpeg?auto=compress&cs=tinysrgb&h=1200&w=900",
    imageRatio: "aspect-[4/5]",
    tag: "Week 1 · Night",
    title: "The elevation trick nobody tells you.",
    story:
      "The Sleep Again wedge is the single item I would fight for. I slept through the night on day four. Recovery moved twice as fast because of it.",
    author: "— Camila · Breast Aug, Age 31",
    pins: ["sleep-again-upper-body-recovery-wedge", "marena-caress-car-b16-pocketed-recovery-bra"],
    palette: "bg-[#FDF3ED]",
  },
  {
    id: "refresh",
    image: "https://images.pexels.com/photos/3762879/pexels-photo-3762879.jpeg?auto=compress&cs=tinysrgb&h=1200&w=900",
    imageRatio: "aspect-[4/5]",
    tag: "Same Day · Post-Injectables",
    title: "Bruise-free by Friday.",
    story:
      "Monday tox, Friday dinner — with the INhance serum and Augustinus Bader on rotation, my bruising was gone in three days. This is the drawer I keep for those weeks.",
    author: "— Lauren · Injectables, Age 41",
    pins: ["alastin-inhance-post-injection-serum", "augustinus-bader-the-wound-gel", "colorescience-sunforgettable-total-protection-spf-50"],
    palette: "bg-white",
  },
];

export default function LookbookPage() {
  const [refills, setRefills] = useState([]);
  const { addRefill } = useCart();

  useEffect(() => { fetchRefills().then(setRefills); }, []);

  const findRefill = (id) => refills.find((r) => r.id === id);

  return (
    <div>
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 pt-16 lg:pt-24 pb-12">
        <div className="max-w-3xl">
          <div className="eyebrow">The Lookbook</div>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight mt-6 leading-[1.02]">
            Recovery <span className="serif-italic text-[#B85C56]">rituals</span>, in real hands.
          </h1>
          <p className="text-[#6B5757] mt-8 leading-relaxed max-w-xl text-lg">
            Six stories from the women who actually use ALTA — the products in each frame are pinned. Tap the heart to add them to your cart.
          </p>
        </div>
      </section>

      {/* Stories — alternating asymmetric layout */}
      <div className="pb-20">
        {STORIES.map((s, idx) => {
          const flipped = idx % 2 === 1;
          return (
            <section
              key={s.id}
              className={`${s.palette} py-16 lg:py-24 border-y border-[#F0DFD0]`}
              data-testid={`lookbook-story-${s.id}`}
            >
              <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                <div className={`${flipped ? "lg:order-2 lg:col-start-7" : ""} lg:col-span-6`}>
                  <div className={`${s.imageRatio} overflow-hidden rounded-xl`}>
                    <img src={s.image} alt={s.title} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                </div>
                <div className={`${flipped ? "lg:col-start-1 lg:row-start-1" : ""} lg:col-span-6`}>
                  <div className="font-mono text-[10px] tracking-[0.32em] uppercase text-[#B85C56]">
                    {s.tag}
                  </div>
                  <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl mt-4 leading-[1.05] tracking-tight">
                    {s.title}
                  </h2>
                  <p className="text-[#6B5757] mt-6 leading-relaxed text-base sm:text-lg font-serif italic">
                    "{s.story}"
                  </p>
                  <div className="font-mono text-[11px] tracking-widest uppercase text-[#A08D8D] mt-4">
                    {s.author}
                  </div>

                  <div className="mt-10">
                    <div className="eyebrow mb-4">Featured in this story</div>
                    <div className="space-y-3">
                      {s.pins.map((pid) => {
                        const r = findRefill(pid);
                        if (!r) return null;
                        return (
                          <div
                            key={pid}
                            data-testid={`lookbook-pin-${pid}`}
                            className="flex items-center gap-4 bg-white/70 backdrop-blur border border-[#F0DFD0] p-3 rounded-lg"
                          >
                            <img src={r.image} alt={r.name} className="w-14 h-14 rounded-md object-cover flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="font-mono text-[10px] uppercase tracking-widest text-[#B85C56]">
                                {r.brand}
                              </div>
                              <Link to={`/refills/${r.id}`} className="block">
                                <div className="font-serif text-base text-[#2A1F1F] leading-tight truncate hover:text-[#B85C56] transition-colors">
                                  {r.name}
                                </div>
                              </Link>
                              <div className="font-mono text-xs text-[#6B5757] mt-1">
                                {formatUsd(r.price)}
                              </div>
                            </div>
                            <button
                              data-testid={`lookbook-add-${pid}`}
                              onClick={() => {
                                addRefill(r, 1);
                                toast.success(`${r.brand} · ${r.name} added`);
                              }}
                              aria-label={`Add ${r.name}`}
                              className="w-10 h-10 rounded-full border border-[#2A1F1F] text-[#2A1F1F] flex items-center justify-center hover:bg-[#2A1F1F] hover:text-[#FBF6F1] transition-colors flex-shrink-0"
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </section>
          );
        })}
      </div>

      {/* CTA */}
      <section className="max-w-3xl mx-auto text-center px-4 sm:px-8 py-20">
        <div className="eyebrow">Your Story Starts Here</div>
        <h2 className="font-serif text-4xl sm:text-5xl mt-4 leading-[1.05]">
          Ready to build your own <span className="serif-italic text-[#B85C56]">recovery ritual?</span>
        </h2>
        <div className="mt-10 flex flex-wrap justify-center gap-4">
          <Link to="/shop" className="btn-primary inline-flex items-center gap-3">
            Shop Systems <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/refills" className="btn-outline">
            Browse Refills
          </Link>
        </div>
      </section>
    </div>
  );
}
